// The "get the app" popup on /umanitoba.
//
// A HOST concern, deliberately outside src/pathfinder/: which app is being
// advertised, and where its store pages are, would otherwise follow the map
// into somebody else's product (the same rule `appPromo` and `support`
// follow). It is rendered over the pathfinder by PathfinderPage and knows
// nothing about it.
//
// ⚠️ Not the pathfinder's own feedback popup. That one (`enableFeedback`) is
// still off, and the two must not both appear: this is one interruption, on
// purpose, for a product that has just gone on the stores.
//
// How it behaves, and why:
//
//  · The corner holds a 5-second countdown, drawn as a ring draining around
//    the number, and nothing dismisses the card while it runs — not the
//    backdrop, not Escape. At zero the ring becomes an ✕ and every way out
//    works. Five seconds is long enough to be read and short enough not to be
//    resented; a card that can be swatted at 0 s is a card nobody reads.
//  · Both store badges (components/StoreBadges.jsx, drawn like the stores'
//    own) are always there, the visitor's own platform first (`detectOs`,
//    user-agent only). The App Store listing opens fine in a desktop
//    browser, and Google Play installs to a signed-in phone from the web, so
//    a laptop gets the same two badges.
//  · Tapping a store button counts as reading it: the card is dismissed on the
//    way out so it is not still there when the tab comes back.
//  · Dismissal is remembered for SEEN_FOR_MS (a week) in localStorage, with
//    every read and write in try/catch: blocked storage (a private window)
//    means the card shows again next visit, which is the honest fallback.
//    `shouldShowAppPopup` / `markAppPopupSeen` are the two pure halves, so
//    the test and the page share one rule.
//  · Inline `<style>`, like MobilePage: the CSP allows 'unsafe-inline' for
//    styles and nothing else here needs a script.

import { useEffect, useRef, useState } from 'react';
import { detectOs } from '../utils/appStores.js';
import StoreBadges from './StoreBadges.jsx';

/** How long the card holds before it can be closed. */
export const POPUP_HOLD_MS = 5000;
/** How long a dismissal is remembered. */
export const SEEN_FOR_MS = 7 * 24 * 60 * 60 * 1000;
export const SEEN_KEY = 'umaps-app-popup-seen';

/** Whether to show the card: not within SEEN_FOR_MS of the last dismissal. */
export function shouldShowAppPopup(storage = safeStorage(), now = Date.now()) {
  try {
    const seen = Number(storage?.getItem(SEEN_KEY));
    return !(Number.isFinite(seen) && seen > 0 && now - seen < SEEN_FOR_MS);
  } catch {
    return true;
  }
}

export function markAppPopupSeen(storage = safeStorage(), now = Date.now()) {
  try { storage?.setItem(SEEN_KEY, String(now)); } catch { /* blocked storage: shows again next time */ }
}

function safeStorage() {
  try { return typeof window !== 'undefined' ? window.localStorage : null; } catch { return null; }
}

export default function AppDownloadPopup({ onDismiss, holdMs = POPUP_HOLD_MS }) {
  const [left, setLeft] = useState(Math.ceil(holdMs / 1000));
  const closable = left <= 0;
  const os = detectOs();
  const closeBtn = useRef(null);

  // One tick a second, counted from mount — not from the last render — so a
  // slow first paint cannot stretch five seconds into seven.
  useEffect(() => {
    const t0 = Date.now();
    const id = setInterval(() => {
      const remaining = Math.ceil((holdMs - (Date.now() - t0)) / 1000);
      setLeft(Math.max(0, remaining));
      if (remaining <= 0) clearInterval(id);
    }, 250);
    return () => clearInterval(id);
  }, [holdMs]);

  // Escape closes once it may; focus lands on the ✕ the moment it exists, so
  // a keyboard user is one key from gone.
  useEffect(() => {
    if (!closable) return undefined;
    closeBtn.current?.focus?.();
    const onKey = (e) => { if (e.key === 'Escape') dismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closable]);

  const dismiss = () => { markAppPopupSeen(); onDismiss?.(); };
  // A store tap is the card doing its job; it goes on the way out.
  const tapped = () => { markAppPopupSeen(); onDismiss?.(); };

  // The ring drains with the seconds: SVG stroke-dashoffset over a circle of
  // circumference 2π·14 ≈ 88, so each second is a fifth of the way round.
  const frac = Math.max(0, Math.min(1, left / Math.ceil(holdMs / 1000)));

  return (
    <div className="pfp-scrim" role="presentation" onClick={closable ? dismiss : undefined}>
      <style>{CSS}</style>
      <div
        className="pfp-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pfp-title"
        onClick={(e) => e.stopPropagation()}
      >
        {closable ? (
          <button ref={closeBtn} type="button" className="pfp-x" onClick={dismiss} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
              <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            </svg>
          </button>
        ) : (
          <div className="pfp-timer" aria-live="polite" aria-label={`Closes in ${left} seconds`} data-left={left}>
            <svg width="36" height="36" viewBox="0 0 36 36" aria-hidden="true">
              <circle cx="18" cy="18" r="14" fill="none" stroke="#dbe3f0" strokeWidth="3" />
              <circle cx="18" cy="18" r="14" fill="none" stroke="#1a73e8" strokeWidth="3"
                      strokeLinecap="round" strokeDasharray="88" strokeDashoffset={88 * (1 - frac)}
                      transform="rotate(-90 18 18)" />
            </svg>
            <span>{left}</span>
          </div>
        )}

        <img className="pfp-logo" src="/umaps-icon-128.png" alt="" width={56} height={56} />
        <h2 id="pfp-title" className="pfp-title">UMaps is now an app</h2>
        <p className="pfp-body">
          Indoor directions, live buses and the campus shuttle, your timetable on the map,
          and widgets for your home screen. Free on both stores.
        </p>
        <StoreBadges first={os} onTap={tapped} className="pfp-stores sb-fill" />
        {closable && (
          <button type="button" className="pfp-later" onClick={dismiss}>Not now</button>
        )}
      </div>
    </div>
  );
}

const CSS = `
.pfp-scrim {
  position: fixed; inset: 0; z-index: 10000;
  display: flex; align-items: center; justify-content: center;
  padding: 20px; box-sizing: border-box;
  background: rgba(15, 30, 56, 0.45);
  -webkit-backdrop-filter: blur(2px); backdrop-filter: blur(2px);
  animation: pfp-in .2s ease-out;
}
@keyframes pfp-in { from { opacity: 0; } to { opacity: 1; } }
.pfp-card {
  position: relative; width: min(380px, 100%);
  background: #fff; color: #16223a; border-radius: 20px;
  padding: 26px 22px 20px; box-sizing: border-box;
  box-shadow: 0 18px 50px rgba(10, 25, 50, 0.3);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  text-align: center;
  animation: pfp-up .24s cubic-bezier(.2,.8,.3,1);
}
@keyframes pfp-up { from { transform: translateY(14px); opacity: 0; } to { transform: none; opacity: 1; } }
/* The corner: the countdown and, later, the ✕ occupy the SAME 36px box, so
   the ring becoming a button is one thing changing state, not two things. */
.pfp-timer, .pfp-x {
  position: absolute; top: 12px; right: 12px;
  width: 36px; height: 36px; border-radius: 999px;
  display: flex; align-items: center; justify-content: center;
}
.pfp-timer { color: #1a56c4; font-size: 13px; font-weight: 800; }
.pfp-timer svg { position: absolute; inset: 0; }
.pfp-timer span { position: relative; }
.pfp-x {
  border: 1px solid #dbe3f0; background: #f4f7fc; color: #37435c; cursor: pointer;
  padding: 0; -webkit-tap-highlight-color: transparent;
  animation: pfp-pop .18s ease-out;
}
@keyframes pfp-pop { from { transform: scale(0.6); opacity: 0; } to { transform: none; opacity: 1; } }
.pfp-x:hover { background: #e8eef9; }
.pfp-x:focus-visible { outline: 2px solid #1a73e8; outline-offset: 2px; }
.pfp-logo { display: block; margin: 0 auto 10px; border-radius: 14px; }
.pfp-title { font-size: 21px; font-weight: 900; letter-spacing: -0.02em; margin: 0 0 8px; color: #0f1e38; }
.pfp-body { font-size: 14px; line-height: 1.5; color: #4a5670; margin: 0 0 16px; }
.pfp-stores { gap: 9px; }
.pfp-later {
  margin: 14px auto 0; display: block; background: none; border: none; cursor: pointer;
  font: inherit; font-size: 13px; font-weight: 700; color: #6b7788; padding: 6px 10px;
  -webkit-tap-highlight-color: transparent;
}
.pfp-later:hover { color: #37435c; }
@media (prefers-reduced-motion: reduce) {
  .pfp-scrim, .pfp-card, .pfp-x { animation: none; }
}
`;
